// cspell:disable

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import blogFetch from '../../axios/config';
import toast from 'react-hot-toast';
import { ModalApontamentoInspFinalProps, PecaInspFinal } from './Types';

const ModalApontamentoInspFinal: React.FC<ModalApontamentoInspFinalProps> = ({ show, onHide }) => {

    const [ordem, setOrdem] = useState<string>("");
    const [dto, setDto] = useState<PecaInspFinal | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);
    const apontandoRef = useRef<boolean>(false);
    const [matricula, setMatricula] = useState<string>("");
    const [matriculaSAP, setMatriculaSAP] = useState<string>("");
    const [nomeUsuario, setNomeUsuario] = useState<string>("");
    const [validandoMatricula, setValidandoMatricula] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    const validarMatriculaSAP = useCallback(async (matricula: string): Promise<{ nome: string; matriculaSAP: string } | null> => {
        const valor = matricula.trim();
        if (!/^\d{6}$/.test(valor)) return null;
        try {
            const { data } = await blogFetch.get(
                `IAcompanhamentoMontagem/validate-matricula/${encodeURIComponent(valor)}`
            );
            if (!data?.exists) return null;
            return {
                nome: data.nome ?? "",
                matriculaSAP: data.matricula_SAP ?? "",
            };
        } catch (err) {
            // 404 (exists:false) cai aqui via axios; tratamos como inválida
            return null;
        }
    }, []);

    const dispararApontamento = async () => {
        if (apontandoRef.current) return;

        const ordemAtual = ordem.trim();
        if (!ordemAtual) {
            toast.error("Por favor, insira a ordem da peça.");
            return;
        }
        if (!/^\d{6}$/.test(matricula) || !matriculaSAP) {
            toast.error("Registre uma matrícula SAP válida.");
            return;
        }

        apontandoRef.current = true;
        setIsSubmitting(true);
        try {
            // Se a ordem ainda não foi carregada (leitor rápido antes do debounce), busca agora.
            let dadosOrdem = dto;
            if (!dadosOrdem) {
                dadosOrdem = await buscarPeca(ordemAtual);
                if (!dadosOrdem) return;
            }

            const formData = {
                Centro_Trabalho: dto?.setorAtual,
                Nro_Matricula: user.user_SAPMatricula,
                Ordem_Fabricacao: ordemAtual,
                Operacao: "4100",
                Tipo_Apontamento: "APROVADO",
                Chave_Modelo: dto?.chaveModelo,
                Integration_Status: "NA FILA",
                _ts_Creat: new Date(Date.now() - 3 * 3600 * 1000),
                _Creat: user.user_UserName ?? user.user_Matricula
            };


            const response = await blogFetch.post('Quality/create', formData);

            if (response.data.success) {
                toast.success(response.data.message);
                resetForm();
                inputRef.current?.focus();
                window.location.href = `sagaprintserver:BR_Etiqueta_IF;SAGA_User;${formData.Ordem_Fabricacao}`;
                
            } else {
                toast.error(response.data.message);
            }
        } catch (error: any) {
            if (error?.response?.status === 409) {
                toast.error(error?.response?.data?.message ?? "Ordem já está apontada!");
                window.location.href = `sagaprintserver:BR_Etiqueta_IF;SAGA_User;${ordemAtual}`;
                return;
            }
            console.error("Erro ao realizar apontamento:", error);
            toast.error(error?.response?.data?.message ?? "Ocorreu um erro inesperado. Tente novamente.");
        } finally {
            setIsSubmitting(false);
            apontandoRef.current = false;
        }
    };

    const handleSubmit = () => dispararApontamento();

    const handleOrdemKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        const ordemAtual = ordem.trim();
        if (!ordemAtual) {
            toast.error("Por favor, insira a ordem da peça.");
            return;
        }
        buscarPeca(ordemAtual);
    };

    const resetForm = () => {
        setOrdem("");
        setDto(null);
    };

    const handleCancel = () => {
        resetForm();
        onHide();
    };

    // Aceita apenas números, máximo 6 dígitos; limpa a validação anterior ao editar
    const handleMatriculaChange = (valor: string) => {
        setMatricula(valor.replace(/\D/g, "").slice(0, 6));
        setMatriculaSAP("");
        setNomeUsuario("");
    };

    // Ao abrir o modal, prefill da matrícula salva (compartilhada com o 14MONTAG) e re-valida
    useEffect(() => {
        if (!show) return;
        const matriculaStorage = localStorage.getItem("listaMontagem_matriculas_SAP");
        if (!matriculaStorage) return;
        let valor = "";
        let nomeSalvo = "";
        let matriculaSAPSalva = "";
        try {
            const obj = JSON.parse(matriculaStorage);
            valor = obj.principal || obj.secundaria || "";
            nomeSalvo = obj.nome || "";
            matriculaSAPSalva = obj.matriculaSAP || "";
        } catch {
            valor = matriculaStorage;
        }
        if (!valor) return;

        setMatricula(valor);
        setNomeUsuario(nomeSalvo); // mostra o nome salvo enquanto re-valida
        setMatriculaSAP(matriculaSAPSalva);

        (async () => {
            const resultado = await validarMatriculaSAP(valor);
            if (resultado === null) {
                localStorage.removeItem("listaMontagem_matriculas_SAP");
                setMatricula("");
                setNomeUsuario("");
                setMatriculaSAP("");
                toast.error("Matrícula não encontrada no SAP");
            } else {
                setNomeUsuario(resultado.nome);
                setMatriculaSAP(resultado.matriculaSAP);
                localStorage.setItem(
                    "listaMontagem_matriculas_SAP",
                    JSON.stringify({ principal: valor, nome: resultado.nome, matriculaSAP: resultado.matriculaSAP })
                );
            }
        })();
    }, [show, validarMatriculaSAP]);

    // Valida automaticamente (debounce) assim que a matrícula chega a 6 dígitos
    useEffect(() => {
        if (!/^\d{6}$/.test(matricula) || matriculaSAP) return;
        const timeout = setTimeout(async () => {
            setValidandoMatricula(true);
            const resultado = await validarMatriculaSAP(matricula);
            setValidandoMatricula(false);
            if (resultado === null) {
                setNomeUsuario("");
                setMatriculaSAP("");
                toast.error("Matrícula não encontrada no SAP");
                return;
            }
            setNomeUsuario(resultado.nome);
            setMatriculaSAP(resultado.matriculaSAP);
            localStorage.setItem(
                "listaMontagem_matriculas_SAP",
                JSON.stringify({ principal: matricula, nome: resultado.nome, matriculaSAP: resultado.matriculaSAP })
            );
            toast.success(`Matrícula registrada: ${resultado.nome}`);
        }, 600);
        return () => clearTimeout(timeout);
    }, [matricula, matriculaSAP, validarMatriculaSAP]);


    useEffect(() => {
        if (!show || ordem.trim().length < 8) return;
        const ordemAtual = ordem.trim();
        const timeout = setTimeout(() => {
            buscarPeca(ordemAtual);
        }, 800);
        return () => clearTimeout(timeout);
    }, [ordem, show]);

    const buscarPeca = async (ordem: string): Promise<PecaInspFinal | null> => {
        try {
            const { data } = await blogFetch.get<PecaInspFinal>(`Quality/get/${encodeURIComponent(ordem)}`);
            if (!data) {
                toast.error("Ordem não encontrada!");
                setDto(null);
                return null;
            }
            setDto(data);
            return data;
        } catch (error: any) {
            setDto(null);
            if (error?.response?.status === 404) {
                toast.error("Ordem não encontrada!");
                return null;
            }
            console.error("Erro ao buscar ordem:", error);
            toast.error("Erro ao buscar ordem.");
            return null;
        }
    };

    return (
        <Modal show={show} onHide={handleCancel} centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Apontamento Inspeção Final</Modal.Title>
            </Modal.Header>
            <Modal.Body>

                <div className="card p-3 shadow-sm rounded-3 mb-3">
                    <Form onSubmit={(e: any) => e.preventDefault()}>
                        <Form.Group className="mb-3">
                            <Form.Label>Ordem</Form.Label>
                            <Form.Control
                                type="text"
                                value={ordem}
                                ref={inputRef}
                                onChange={(e) => { setOrdem(e.target.value); setDto(null); }}
                                onKeyDown={handleOrdemKeyDown}
                                placeholder="Bipe ou digite a ordem"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Matrícula SAP</Form.Label>
                            <Form.Control
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={matricula}
                                onChange={(e) => handleMatriculaChange(e.target.value)}
                                placeholder="Digite a matrícula SAP (6 dígitos)"
                                isInvalid={matricula.length === 6 && !validandoMatricula && !matriculaSAP}
                                isValid={!!matriculaSAP}
                            />
                            <Form.Control.Feedback type="invalid">
                                Matrícula não encontrada no SAP.
                            </Form.Control.Feedback>
                            {validandoMatricula ? (
                                <small className="text-muted d-block mt-1">Validando matrícula...</small>
                            ) : nomeUsuario ? (
                                <small className="text-success fw-semibold d-block mt-1">
                                    Operador: {nomeUsuario}
                                </small>
                            ) : null}
                        </Form.Group>
                    </Form>

                    <div className="card p-3 shadow-sm rounded-3">
                        <h6 className="fw-bold text-secondary mb-3">Informações da Peça</h6>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold text-muted">Tipo Peça</label>
                                <input value={dto?.tipoPeca ?? ""} className="form-control" readOnly />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold text-muted">Código Veículo</label>
                                <input value={dto?.codigoVeiculo ?? ""} className="form-control" readOnly />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold text-muted">Ultimo Apontamento</label>
                                <input value={dto?.ultimoApont ?? ""} className="form-control" readOnly />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label small fw-semibold text-muted">Projeto</label>
                                <input value={dto?.projeto ?? ""} className="form-control" readOnly />
                            </div>
                            <div className="col-12">
                                <label className="form-label small fw-semibold text-muted">Veículo</label>
                                <input value={dto?.veiculo ?? ""} className="form-control" readOnly />
                            </div>
                        </div>
                    </div>

                </div>

            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                <button className="btn btn-outline-secondary px-4" onClick={handleCancel}>Cancelar</button>
                <button
                    className="btn btn-success px-5 fw-semibold"
                    style={{ letterSpacing: '0.05em' }}
                    onClick={handleSubmit}
                    disabled={!matriculaSAP || validandoMatricula || !dto || isSubmitting}
                    title={
                        !matriculaSAP
                            ? "Valide a matrícula SAP para apontar"
                            : !dto
                                ? "Informe uma ordem válida com os dados da peça para apontar"
                                : undefined
                    }
                >
                    ✓ Apontar
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalApontamentoInspFinal;
